import React, { useState } from 'react';
import {
  updateScheduledTask,
  ScheduledTask,
  ScheduleConfig,
} from '../../services/scheduledTasksApi';
import { getSkillDisplay } from './skillDisplayMeta';
import { Button, Modal } from '../ui';
import { formFieldStyles as ff } from './formFieldStyles';

interface EditScheduleModalProps {
  task: ScheduledTask;
  onClose: () => void;
  onSuccess: () => void;
  showError?: (notification: any) => void;
}

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Phoenix', label: 'Arizona (No DST)' },
  { value: 'America/Anchorage', label: 'Alaska Time' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time' },
  { value: 'UTC', label: 'UTC' },
];

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sun', fullLabel: 'Sunday' },
  { value: 1, label: 'Mon', fullLabel: 'Monday' },
  { value: 2, label: 'Tue', fullLabel: 'Tuesday' },
  { value: 3, label: 'Wed', fullLabel: 'Wednesday' },
  { value: 4, label: 'Thu', fullLabel: 'Thursday' },
  { value: 5, label: 'Fri', fullLabel: 'Friday' },
  { value: 6, label: 'Sat', fullLabel: 'Saturday' },
];

const EditScheduleModal: React.FC<EditScheduleModalProps> = ({
  task,
  onClose,
  onSuccess,
  showError,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [customSubject, setCustomSubject] = useState(task.customSubject || '');
  const [scheduleType, setScheduleType] = useState<'once' | 'weekly' | 'monthly'>(
    task.schedule.type === 'daily' ? 'weekly' : task.schedule.type as 'once' | 'weekly' | 'monthly'
  );
  const [time, setTime] = useState(task.schedule.time);
  const [timezone, setTimezone] = useState(task.schedule.timezone);
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>(task.schedule.daysOfWeek || [1]);
  const [dayOfMonth, setDayOfMonth] = useState(task.schedule.dayOfMonth || 1);
  const [runDate, setRunDate] = useState<string>(() => {
    if (task.schedule.startDate) return task.schedule.startDate.split('T')[0];
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  });

  const toggleDayOfWeek = (day: number) => {
    setDaysOfWeek(prev => {
      if (prev.includes(day)) {
        if (prev.length === 1) return prev;
        return prev.filter(d => d !== day);
      }
      return [...prev, day].sort((a, b) => a - b);
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (scheduleType === 'weekly' && daysOfWeek.length === 0) {
      showError?.({ title: 'Validation Error', message: 'Please select at least one day of the week' });
      return;
    }

    const schedule: ScheduleConfig = {
      type: scheduleType,
      time,
      timezone,
      daysOfWeek: scheduleType === 'weekly' ? daysOfWeek : undefined,
      dayOfMonth: scheduleType === 'monthly' ? dayOfMonth : undefined,
      startDate: scheduleType === 'once' ? runDate : undefined,
    };

    setSubmitting(true);
    try {
      await updateScheduledTask(task.id, {
        schedule,
        customSubject: customSubject.trim() || null,
      });
      onSuccess();
    } catch (err: any) {
      showError?.({ title: 'Error', message: err.message || 'Failed to update schedule' });
    } finally {
      setSubmitting(false);
    }
  };

  const styles: { [key: string]: React.CSSProperties } = {
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1.25rem',
    },
    taskInfo: {
      fontSize: '0.8125rem',
      color: 'var(--fg-2)',
      backgroundColor: 'var(--bg-surface-3)',
      padding: '0.625rem 0.75rem',
      borderRadius: 'var(--radius-md)',
      lineHeight: 1.4,
    },
    taskInfoLabel: {
      fontWeight: 600,
      color: 'var(--fg-1)',
    },
    scheduleRow: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '1rem',
    },
    daysContainer: {
      display: 'flex',
      flexWrap: 'wrap' as const,
      gap: '0.5rem',
    },
    dayButton: {
      padding: '0.5rem 0.75rem',
      borderRadius: 'var(--radius-md)',
      border: '1px solid var(--border-2)',
      backgroundColor: 'var(--cg-neutral-0)',
      color: 'var(--fg-1)',
      fontSize: '0.75rem',
      fontWeight: 500,
      cursor: 'pointer',
      minWidth: '44px',
      textAlign: 'center' as const,
      transition: 'background 160ms var(--ease-standard), border-color 160ms var(--ease-standard)',
      fontFamily: 'var(--font-sans)',
    },
    dayButtonSelected: {
      backgroundColor: 'var(--accent)',
      borderColor: 'var(--accent)',
      color: '#fff',
    },
    quickSelectRow: {
      display: 'flex',
      gap: '0.5rem',
      marginTop: '0.5rem',
    },
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Edit schedule"
      eyebrow="Scheduled task"
      width={480}
      closeOnBackdrop={false}
      closeOnEsc={false}
      footer={
        <>
          <Button variant="ghost" size="md" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" size="md" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Saving...' : 'Save schedule'}
          </Button>
        </>
      }
    >
      <form style={styles.form} onSubmit={handleSubmit}>
        <div style={styles.taskInfo}>
          <span style={styles.taskInfoLabel}>{getSkillDisplay(task.skillName).displayName}</span>
          {' — '}
          {task.prompt && task.prompt.trim()
            ? (task.prompt.length > 80 ? task.prompt.slice(0, 80) + '…' : task.prompt)
            : getSkillDisplay(task.skillName).shortDescription}
        </div>

        <div style={ff.group}>
          <label style={ff.label}>Email Subject (optional)</label>
          <input
            style={ff.field}
            type="text"
            placeholder="Custom email subject line"
            value={customSubject}
            onChange={(e) => setCustomSubject(e.target.value)}
            maxLength={200}
          />
          <p style={ff.hint}>Leave blank to use the default subject.</p>
        </div>

        <div style={ff.group}>
          <label style={ff.label}>Schedule Type</label>
          <select
            style={ff.field}
            value={scheduleType}
            onChange={(e) => setScheduleType(e.target.value as any)}
          >
            <option value="once">Run Once</option>
            <option value="weekly">Select Days</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>

        <div style={styles.scheduleRow}>
          <div style={ff.group}>
            <label style={ff.label}>Time</label>
            <input
              style={ff.field}
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
            />
          </div>

          <div style={ff.group}>
            <label style={ff.label}>Timezone</label>
            <select
              style={ff.field}
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
            >
              {TIMEZONES.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {scheduleType === 'once' && (
          <div style={ff.group}>
            <label style={ff.label}>Run Date</label>
            <input
              style={ff.field}
              type="date"
              value={runDate}
              min={new Date().toISOString().split('T')[0]}
              onChange={(e) => setRunDate(e.target.value)}
            />
          </div>
        )}

        {scheduleType === 'weekly' && (
          <div style={ff.group}>
            <label style={ff.label}>Days of Week</label>
            <div style={styles.daysContainer}>
              {DAYS_OF_WEEK.map((day) => (
                <button
                  key={day.value}
                  type="button"
                  style={{
                    ...styles.dayButton,
                    ...(daysOfWeek.includes(day.value) ? styles.dayButtonSelected : {}),
                  }}
                  onClick={() => toggleDayOfWeek(day.value)}
                  title={day.fullLabel}
                >
                  {day.label}
                </button>
              ))}
            </div>
            <div style={styles.quickSelectRow}>
              <Button variant="subtle" size="sm" onClick={() => setDaysOfWeek([1, 2, 3, 4, 5])}>
                Weekdays
              </Button>
              <Button variant="subtle" size="sm" onClick={() => setDaysOfWeek([0, 6])}>
                Weekends
              </Button>
              <Button variant="subtle" size="sm" onClick={() => setDaysOfWeek([0, 1, 2, 3, 4, 5, 6])}>
                Every Day
              </Button>
            </div>
          </div>
        )}

        {scheduleType === 'monthly' && (
          <div style={ff.group}>
            <label style={ff.label}>Day of Month</label>
            <select
              style={ff.field}
              value={dayOfMonth}
              onChange={(e) => setDayOfMonth(Number(e.target.value))}
            >
              {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => {
                const suffix = (day % 10 === 1 && day !== 11) ? 'st' :
                               (day % 10 === 2 && day !== 12) ? 'nd' :
                               (day % 10 === 3 && day !== 13) ? 'rd' : 'th';
                return (
                  <option key={day} value={day}>
                    {day}{suffix}
                  </option>
                );
              })}
            </select>
          </div>
        )}
      </form>
    </Modal>
  );
};

export default EditScheduleModal;
